import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/pro-solid-svg-icons/faSpinner";
import { Button } from "@wondermaps/ui";
import type { SponsorLogo, SponsorTierGroup } from "./sponsor-types";
import { SIZE_BUCKET_CLASSES } from "./sponsor-size-buckets";

interface Branding {
  logoUrl?: string | null;
  appName?: string | null;
  brandColor?: string | null;
  splashBackground?: string | null;
}

type SplashState = "loading" | "ready" | "error" | "offline-error";

interface SplashScreenProps {
  branding?: Branding | null;
  state: SplashState;
  isFromCache?: boolean;
  onRetry?: () => void;
  onContinue?: () => void;
  showPoweredBy?: boolean;
  sponsors?: SponsorTierGroup[];
  onSponsorImpression?: (sponsors: SponsorLogo[], placement: string) => void;
  brandAccent?: string | null;
}

const DEFAULT_MIN_DISPLAY_MS = 500;
const CACHED_MIN_DISPLAY_MS = 300;
const TIMEOUT_WARNING_MS = 5000;

export function SplashScreen({
  branding,
  state,
  isFromCache = false,
  onRetry,
  onContinue,
  showPoweredBy = true,
  sponsors,
  onSponsorImpression,
  brandAccent,
}: SplashScreenProps) {
  const { t } = useTranslation("common");
  const [minTimePassed, setMinTimePassed] = useState(false);
  const [showTimeout, setShowTimeout] = useState(false);

  const minDisplayMs = isFromCache ? CACHED_MIN_DISPLAY_MS : DEFAULT_MIN_DISPLAY_MS;

  useEffect(() => {
    const minTimer = setTimeout(() => {
      setMinTimePassed(true);
    }, minDisplayMs);

    return () => clearTimeout(minTimer);
  }, [minDisplayMs]);

  useEffect(() => {
    if (state === "loading") {
      const timeoutTimer = setTimeout(() => {
        setShowTimeout(true);
      }, TIMEOUT_WARNING_MS);

      return () => clearTimeout(timeoutTimer);
    }
    setShowTimeout(false);
  }, [state]);

  const hasBranding = branding != null;
  const bgColor = branding?.splashBackground || "#ffffff";
  const textColor = branding?.brandColor || "#1f2937";
  const appName = branding?.appName || "Wondermaps";
  const logoUrl = branding?.logoUrl ?? null;

  // Ready state: fade out when min time passed
  const shouldHide = state === "ready" && minTimePassed;

  // Notify parent when auto-dismissing
  useEffect(() => {
    if (shouldHide && onContinue) {
      onContinue();
    }
  }, [shouldHide, onContinue]);

  if (shouldHide) {
    return null;
  }

  const hasSponsors = sponsors && sponsors.some((t) => t.sponsors.length > 0);

  return (
    <div
      data-testid="splash-screen"
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center transition-opacity duration-300"
      style={{ backgroundColor: bgColor }}
    >
      <div className="flex flex-1 flex-col items-center justify-center gap-6">
        {hasBranding && logoUrl && (
          <img
            src={logoUrl}
            alt={appName}
            className="max-h-24 max-w-48 object-contain"
          />
        )}

        {hasBranding && (
          <h1 className="text-2xl font-semibold" style={{ color: textColor }}>
            {appName}
          </h1>
        )}

        {state === "loading" && !showTimeout && (
          <FontAwesomeIcon icon={faSpinner} spin className="h-8 w-8 text-gray-400" />
        )}

        {state === "loading" && showTimeout && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-gray-500">{t("splash.takingLonger")}</p>
            <div className="flex gap-2">
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  {t("retry")}
                </Button>
              )}
              {onContinue && (
                <Button variant="outline" size="sm" onClick={onContinue}>
                  {t("continueAnyway")}
                </Button>
              )}
            </div>
          </div>
        )}

        {state === "error" && (
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-red-600">{t("splash.failedToLoad")}</p>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                {t("retry")}
              </Button>
            )}
          </div>
        )}

        {state === "offline-error" && (
          <div className="flex flex-col items-center gap-4 text-center">
            <p className="text-sm text-gray-600">{t("offline.noSavedMap")}</p>
            <p className="text-sm text-gray-500">{t("offline.connectToLoad")}</p>
            {onRetry && (
              <Button variant="outline" size="sm" onClick={onRetry}>
                {t("tryAgain")}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Sponsor logos (not clickable during splash) */}
      {hasSponsors && (
        <SplashSponsors sponsors={sponsors!} onImpression={onSponsorImpression} />
      )}

      {showPoweredBy && (
        <div
          className="pb-8 text-sm text-gray-400"
          style={brandAccent ? { color: brandAccent } : undefined}
        >
          {t("poweredByWondermaps")}
        </div>
      )}
    </div>
  );
}

function SplashSponsors({
  sponsors,
  onImpression,
}: {
  sponsors: SponsorTierGroup[];
  onImpression?: (sponsors: SponsorLogo[], placement: string) => void;
}) {
  const { t } = useTranslation("common");
  const allSponsors = useMemo(() => sponsors.flatMap((tier) => tier.sponsors), [sponsors]);

  // eslint-disable-next-line react-hooks/exhaustive-deps — fire-once impression on mount
  useEffect(() => {
    onImpression?.(allSponsors, "splash");
  }, []);

  return (
    <div className="pb-6 flex flex-col items-center gap-3">
      <p className="text-xs text-gray-400">{t("sponsors.presentedBy")}</p>

      {sponsors.map((tier) => {
        if (tier.sponsors.length === 0) return null;
        const sizeClass = SIZE_BUCKET_CLASSES.splash[tier.sizeBucket] ?? SIZE_BUCKET_CLASSES.splash.small;
        return (
          <div key={tier.slug} className="flex items-center gap-3">
            {tier.sponsors.map((s) =>
              s.logoUrl ? (
                <img key={s.id} src={s.logoUrl} alt={s.name || "Sponsor"} className={`${sizeClass} object-contain`} onError={(e) => { e.currentTarget.style.display = "none"; }} />
              ) : null
            )}
          </div>
        );
      })}
    </div>
  );
}
